<script setup lang="ts">
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { UserRole } from '@/types'
import { getNavigationSections, isNavigationItemActive } from '@/utils/navigation'

interface Props {
  userRole: UserRole
}
const props = defineProps<Props>()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const sections = computed(() => getNavigationSections(props.userRole))

function navigateTo(name: string, itemRoute: string) {
  if (!isNavigationItemActive(route.path, itemRoute)) {
    router.push({ name })
  }
}
</script>

<template>
  <nav class="app-navigation" :aria-label="t('nav.mainNavigation')">
    <div class="nav-sections">
      <section
        v-for="section in sections"
        :key="section.key"
        class="nav-section"
        :aria-label="t(section.labelKey)"
      >
        <p class="nav-section-label">{{ t(section.labelKey) }}</p>
        <ul class="nav-list">
          <li v-for="item in section.items" :key="item.name" class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ 'is-active': isNavigationItemActive(route.path, item.route) }"
              :aria-current="isNavigationItemActive(route.path, item.route) ? 'page' : undefined"
              @click="navigateTo(item.name, item.route)"
            >
              <i :class="item.icon" aria-hidden="true"></i>
              <span class="nav-text">{{ t(item.labelKey) }}</span>
            </button>
          </li>
        </ul>
      </section>
    </div>
  </nav>
</template>

<style scoped>
.app-navigation {
  background: var(--surface-card);
  border-bottom: 1px solid var(--neutral-100);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  position: sticky;
  top: var(--header-height, 60px);
  z-index: 90;
}

.nav-sections {
  display: flex;
  gap: 1rem;
  padding: 0.625rem 1rem 0.75rem;
  max-width: 80rem;
  margin: 0 auto;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.nav-sections::-webkit-scrollbar {
  display: none;
}

.nav-section {
  min-width: max-content;
}

.nav-section-label {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-400);
  margin: 0 0 0.375rem 0.5rem;
}

.nav-list {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin: 0;
}

.nav-link {
  background: transparent;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: var(--neutral-500);
  transition: all var(--duration-fast) var(--ease-out);
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  white-space: nowrap;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--color-pg-blue) 0%, var(--color-pg-blue-light) 100%);
  border-radius: 3px 3px 0 0;
  transition: all var(--duration-fast) var(--ease-out);
  transform: translateX(-50%);
}

.nav-link:hover {
  background: linear-gradient(135deg, var(--primary-50) 0%, rgba(0, 61, 165, 0.08) 100%);
  color: var(--color-pg-blue);
  transform: translateY(-1px);
}

.nav-link:focus-visible {
  outline: 2px solid var(--color-pg-blue);
  outline-offset: 2px;
}

.nav-link.is-active {
  color: var(--color-pg-blue);
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  box-shadow: 0 2px 8px rgba(0, 61, 165, 0.15);
}

.nav-link.is-active::after {
  width: 60%;
}

.nav-link i {
  font-size: 1.125rem;
}

@media (max-width: 640px) {
  .app-navigation {
    top: var(--header-height, 56px);
  }

  .nav-sections {
    padding: 0.5rem 0.75rem 0.625rem;
    gap: 0.75rem;
  }

  .nav-section-label {
    margin-left: 0.375rem;
  }

  .nav-link {
    padding: 0.625rem 0.875rem;
  }
}
</style>
